/**
 * StandardsBoard - Pipeline Board for Standards Workflow
 * Displays standards cards in pipeline columns: Draft -> Review -> Approved -> Published
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiPlus, FiRefreshCw, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { useStatus } from '../contexts/StatusProvider';
import CardDetailModal from '../components/CardDetailModal';
import NewCardModal from '../components/NewCardModal';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface Card {
  card_id: string;
  card_type: string;
  title: string;
  description?: string;
  state: string;
  priority: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Standards pipeline columns configuration
const STANDARDS_COLUMNS = [
  { id: 'draft', title: 'Draft', bgColor: 'bg-gray-50', description: 'Initial drafts being written' },
  { id: 'review', title: 'Review', bgColor: 'bg-yellow-50', description: 'Under review by stakeholders' },
  { id: 'approved', title: 'Approved', bgColor: 'bg-blue-50', description: 'Approved, pending publication' },
  { id: 'published', title: 'Published', bgColor: 'bg-green-50', description: 'Active and published' }
];

const StandardsBoard: React.FC = () => {
  const { Client_ID, Auth_Status } = useStatus();
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [showNewCardModal, setShowNewCardModal] = useState(false);
  const [movingCard, setMovingCard] = useState<string | null>(null);

  // Get effective client ID (from StatusProvider or localStorage for super admin context)
  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId');

  // Fetch standards cards
  const fetchCards = useCallback(async () => {
    if (!Auth_Status || !effectiveClientId) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await Make_Authorized_API_Call<Card>(
        API_ENDPOINTS.EA_AGILE.CARDS,
        'GET',
        undefined,
        { params: { Client_ID: effectiveClientId, card_type: 'STANDARD' } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch standards');
      }

      // Filter to only standard cards (in case API doesn't filter)
      const standardCards = (response.data.Records || []).filter(
        (card: Card) => card.card_type.toLowerCase() === 'standard'
      );
      setCards(standardCards);
    } catch (err) {
      console.error('Error fetching standards:', err);
      setError(err instanceof Error ? err.message : 'Failed to load standards');
    } finally {
      setIsLoading(false);
    }
  }, [Auth_Status, effectiveClientId]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // Move card to a different state
  const moveCard = async (cardId: string, newState: string) => {
    if (!effectiveClientId) return;

    setMovingCard(cardId);

    try {
      const response = await Make_Authorized_API_Call<Card>(
        API_ENDPOINTS.CARDS.UPDATE,
        'PUT',
        {
          Client_ID: effectiveClientId,
          card_id: cardId,
          state: newState,
        }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to move card');
      }

      // Update local state
      setCards(prevCards =>
        prevCards.map(card =>
          card.card_id === cardId ? { ...card, state: newState } : card
        )
      );
    } catch (err) {
      console.error('Error moving card:', err);
      setError(err instanceof Error ? err.message : 'Failed to move card');
    } finally {
      setMovingCard(null);
    }
  };

  // Get next/previous column for a card
  const getAdjacentColumn = (currentState: string, direction: 'next' | 'prev'): string | null => {
    const currentIndex = STANDARDS_COLUMNS.findIndex(col => col.id === currentState);
    if (currentIndex === -1) return null;

    if (direction === 'next' && currentIndex < STANDARDS_COLUMNS.length - 1) {
      return STANDARDS_COLUMNS[currentIndex + 1].id;
    }
    if (direction === 'prev' && currentIndex > 0) {
      return STANDARDS_COLUMNS[currentIndex - 1].id;
    }
    return null;
  };

  // Group cards by column
  const getCardsForColumn = (columnId: string): Card[] => {
    return cards.filter(card => card.state === columnId);
  };

  // Calculate stats
  const stats = useMemo(() => {
    return {
      draft: cards.filter(c => c.state === 'draft').length,
      review: cards.filter(c => c.state === 'review').length,
      approved: cards.filter(c => c.state === 'approved').length,
      published: cards.filter(c => c.state === 'published').length,
      total: cards.length,
    };
  }, [cards]);

  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded';
      case 'high':
        return 'bg-orange-100 text-orange-800 text-xs px-2 py-0.5 rounded';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded';
      case 'low':
        return 'bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded';
      default:
        return 'bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded';
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading standards...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 h-full">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Standards Pipeline</h1>
            <p className="text-gray-600 mt-1">
              Track standards through Draft, Review, Approval, and Publication stages
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={fetchCards}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white"
            >
              <FiRefreshCw className="inline mr-1" />
              Refresh
            </button>
            <button
              onClick={() => setShowNewCardModal(true)}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FiPlus className="inline mr-1" />
              New Standard
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Total Standards</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Draft</div>
            <div className="text-2xl font-bold text-gray-700 mt-1">{stats.draft}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <div className="text-sm text-yellow-700">Pending Review</div>
            <div className="text-2xl font-bold text-yellow-700 mt-1">{stats.review}</div>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="text-sm text-blue-700">Approved</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">{stats.approved}</div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="text-sm text-green-700">Published</div>
            <div className="text-2xl font-bold text-green-700 mt-1">{stats.published}</div>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-800 font-bold">
            x
          </button>
        </div>
      )}

      {/* Pipeline Columns */}
      <div className="flex-1 overflow-x-auto">
        <div className="h-[calc(100vh-380px)] flex p-4 space-x-4 min-w-max">
          {STANDARDS_COLUMNS.map((column) => {
            const columnCards = getCardsForColumn(column.id);

            return (
              <div
                key={column.id}
                className="flex-shrink-0 w-80 flex flex-col"
              >
                {/* Column header */}
                <div className={`${column.bgColor} px-3 py-2 rounded-t-lg border border-gray-200`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{column.title}</h3>
                      <p className="text-xs text-gray-500">{column.description}</p>
                    </div>
                    <span className="text-sm font-bold text-gray-600 bg-white px-2 py-1 rounded">
                      {columnCards.length}
                    </span>
                  </div>
                </div>

                {/* Cards container */}
                <div className="flex-1 bg-white border-x border-b border-gray-200 rounded-b-lg p-2 space-y-2 overflow-y-auto">
                  {columnCards.length === 0 ? (
                    <div className="text-center text-sm text-gray-400 py-8">
                      No standards
                    </div>
                  ) : (
                    columnCards.map((card) => {
                      const prevColumn = getAdjacentColumn(card.state, 'prev');
                      const nextColumn = getAdjacentColumn(card.state, 'next');
                      const isMoving = movingCard === card.card_id;

                      return (
                        <div
                          key={card.card_id}
                          className={`bg-white border border-gray-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow ${
                            isMoving ? 'opacity-50' : ''
                          }`}
                        >
                          {/* Card header */}
                          <div className="flex items-start justify-between mb-2">
                            <span className={getPriorityBadge(card.priority)}>
                              {card.priority}
                            </span>
                          </div>

                          {/* Card title - clickable */}
                          <h4
                            onClick={() => setSelectedCard(card)}
                            className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600"
                          >
                            {card.title}
                          </h4>

                          {/* Card description */}
                          {card.description && (
                            <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                              {card.description}
                            </p>
                          )}

                          {/* Card footer */}
                          <div className="text-xs text-gray-500 flex items-center justify-between pt-2 border-t border-gray-100">
                            <span>{card.created_by}</span>
                            <span>{new Date(card.updated_at).toLocaleDateString()}</span>
                          </div>

                          {/* Move buttons */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                            {prevColumn ? (
                              <button
                                onClick={() => moveCard(card.card_id, prevColumn)}
                                disabled={isMoving}
                                className="text-xs text-gray-500 hover:text-blue-600 flex items-center"
                                title={`Move to ${STANDARDS_COLUMNS.find(c => c.id === prevColumn)?.title}`}
                              >
                                <FiArrowLeft className="mr-1" />
                                Back
                              </button>
                            ) : (
                              <span />
                            )}
                            {nextColumn ? (
                              <button
                                onClick={() => moveCard(card.card_id, nextColumn)}
                                disabled={isMoving}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center font-medium"
                                title={`Move to ${STANDARDS_COLUMNS.find(c => c.id === nextColumn)?.title}`}
                              >
                                Advance
                                <FiArrowRight className="ml-1" />
                              </button>
                            ) : (
                              <span className="text-xs text-green-600 font-medium">
                                Complete
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pipeline flow indicator */}
      <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Pipeline Flow</h3>
        <div className="flex items-center justify-center space-x-4 text-sm">
          <span className="text-gray-600">Draft</span>
          <FiArrowRight className="text-gray-400" />
          <span className="text-yellow-600">Review</span>
          <FiArrowRight className="text-gray-400" />
          <span className="text-blue-600">Approved</span>
          <FiArrowRight className="text-gray-400" />
          <span className="text-green-600">Published</span>
        </div>
      </div>

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
          clientId={effectiveClientId || ''}
        />
      )}

      {/* New Card Modal */}
      {showNewCardModal && (
        <NewCardModal
          onClose={() => setShowNewCardModal(false)}
          onSuccess={fetchCards}
          defaultState="draft"
          defaultCardType="STANDARD"
          clientId={effectiveClientId || ''}
        />
      )}
    </div>
  );
};

export default StandardsBoard;
